'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { Search, Bell, Menu, X, Tv2, Radio, Calendar, Globe, Check } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface Notification {
  id: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type: 'live' | 'event' | 'system';
}

const MOCK_NOTIFICATIONS: Notification[] = [
  { id: 'n1', title: 'New Live Stream', message: 'Courage TV is now live', time: '2m ago', read: false, type: 'live' },
  { id: 'n2', title: 'Live Event Starting', message: 'AFCON 2025 Final starts in 30 minutes', time: '28m ago', read: false, type: 'event' },
  { id: 'n3', title: 'Stream Back Online', message: 'NTA News 24 has resumed streaming', time: '1h ago', read: true, type: 'live' },
  { id: 'n4', title: 'New Radio Station', message: 'Cool FM Lagos added to the platform', time: '3h ago', read: true, type: 'system' },
];

export default function Topbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(MOCK_NOTIFICATIONS);
  const [searchQuery, setSearchQuery] = useState('');
  const notifRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const markRead = (id: string) => {
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/streaming-dashboard?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const notifTypeColor = (type: Notification['type']) => {
    if (type === 'live') return 'bg-red-600';
    if (type === 'event') return 'bg-amber-500';
    return 'bg-blue-500';
  };

  return (
    <header className="h-14 bg-black border-b border-[rgb(38,38,38)] flex items-center px-3 sm:px-4 gap-2 sm:gap-3 flex-shrink-0 z-40 relative">
      {/* Mobile Menu Toggle */}
      <button
        className="lg:hidden text-[rgb(163,163,163)] hover:text-white transition-colors flex-shrink-0"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex-1 max-w-xs sm:max-w-md">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(115,115,115)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search channels, radio, events..."
            className="w-full bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] text-sm text-white placeholder-[rgb(115,115,115)] rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:border-red-600 transition-colors"
          />
        </div>
      </form>

      <div className="flex items-center gap-1 ml-auto">
        {/* Live indicator */}
        <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-[rgba(220,38,38,0.1)] border border-[rgba(220,38,38,0.2)] rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600 live-pulse" />
          <span className="text-red-500 text-xs font-600">47 LIVE</span>
        </div>

        {/* Notification Bell */}
        <div ref={notifRef} className="relative">
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative p-2 text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] rounded-lg transition-all"
            aria-label="Notifications"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-600 rounded-full text-[9px] font-700 text-white flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Notification Panel */}
          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl shadow-2xl z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(38,38,38)]">
                <span className="text-sm font-700 text-white">Notifications</span>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-400 transition-colors"
                  >
                    <Check size={12} />
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-sm text-[rgb(115,115,115)] text-center py-8">No notifications</p>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      onClick={() => markRead(notif.id)}
                      className={`flex items-start gap-3 px-4 py-3 border-b border-[rgb(38,38,38)] cursor-pointer hover:bg-[rgb(24,24,24)] transition-colors ${!notif.read ? 'bg-[rgba(220,38,38,0.04)]' : ''}`}
                    >
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${notifTypeColor(notif.type)}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-600 truncate ${notif.read ? 'text-[rgb(163,163,163)]' : 'text-white'}`}>{notif.title}</p>
                        <p className="text-xs text-[rgb(115,115,115)] mt-0.5 truncate">{notif.message}</p>
                        <p className="text-[10px] text-[rgb(64,64,64)] mt-1">{notif.time}</p>
                      </div>
                      {!notif.read && <div className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0 mt-1.5" />}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      {mobileMenuOpen && (
        <div className="absolute top-14 left-0 right-0 bg-black border-b border-[rgb(38,38,38)] z-50 lg:hidden shadow-xl">
          <nav className="flex flex-col p-3 gap-1">
            {[
              { label: 'Live TV', icon: <Tv2 size={16} />, href: '/streaming-dashboard' },
              { label: 'Radio', icon: <Radio size={16} />, href: '/streaming-dashboard' },
              { label: 'Events', icon: <Calendar size={16} />, href: '/streaming-dashboard' },
              { label: 'Countries', icon: <Globe size={16} />, href: '/streaming-dashboard' },
              { label: 'News', icon: <Tv2 size={16} />, href: '/news' },
            ]?.map((item) => (
              <Link
                key={`mobile-nav-${item?.href}-${item?.label}`}
                href={item?.href}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] transition-all text-sm font-medium"
              >
                {item?.icon}
                {item?.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}